/**
 * Bulk Edit Dialog for Story List
 *
 * Multi-language architecture means phase lives on story_translations.
 * When editing phase, we update ALL translations for each selected story.
 * publishedAt remains on the stories table.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
// Issue #640: Renamed from StoryPhase to StoryTransPhase to clarify scope
import {
  StoryTransPhase,
  STORY_TRANS_PHASE_CONFIG,
  getStoryTransPhaseConfig,
  logger,
  toDbKeys,
  withUpdateTimestamp,
} from '@musedrops/shared';
import { supabase } from '../../../services/supabase';

interface BulkEditDialogProps {
  open: boolean;
  selectedIds: string[];
  onClose: () => void;
  onComplete: () => void;
}

/**
 * Dialog for bulk editing stories (phase and publishedAt)
 *
 * Key behavior:
 * - Phase changes update story_translations for ALL languages of each story
 * - publishedAt changes update the stories table
 */
export const BulkEditDialog = ({
  open,
  selectedIds,
  onClose,
  onComplete,
}: BulkEditDialogProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [updatePhase, setUpdatePhase] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<StoryTransPhase>('META_READY');
  const [updatePublishedAt, setUpdatePublishedAt] = useState(false);
  const [selectedPublishedAt, setSelectedPublishedAt] = useState<string>('');

  const handleClose = () => {
    if (!isProcessing) {
      // Reset form state
      setUpdatePhase(false);
      setSelectedPhase('META_READY');
      setUpdatePublishedAt(false);
      setSelectedPublishedAt('');
      setError(null);
      onClose();
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPublishedAt(e.target.value);
  };

  const handleUpdate = async () => {
    if (!updatePhase && !updatePublishedAt) {
      setError('Please select at least one field to update');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Step 1: Get story slugs for the selected IDs (needed for translation updates)
      const { data: stories, error: fetchError } = await supabase
        .from('stories')
        .select('id, slug')
        .in('id', selectedIds);

      if (fetchError) {
        throw new Error(`Failed to fetch stories: ${fetchError.message}`);
      }

      if (!stories || stories.length === 0) {
        throw new Error('No stories found for the selected IDs');
      }

      const storySlugs = stories.map(s => s.slug);

      // Step 2: Update story_translations if phase is being changed
      // Phase lives on story_translations, update ALL translations
      if (updatePhase) {
        const { error: translationError } = await supabase
          .from('story_translations')
          .update(
            withUpdateTimestamp(
              toDbKeys({
                phase: selectedPhase,
              })
            )
          )
          .in('story_slug', storySlugs);

        if (translationError) {
          throw new Error(
            `Failed to update translations: ${translationError.message}`
          );
        }

        logger.info('Bulk updated story translation phases', {
          count: storySlugs.length,
          phase: selectedPhase,
        });
      }

      // Step 3: Update stories table if publishedAt is being changed
      // Also update publishedAt if phase is READY_FOR_PUBLISH (clear it)
      if (updatePublishedAt || (updatePhase && selectedPhase === 'READY_FOR_PUBLISH')) {
        const storyUpdate: Record<string, unknown> = {};

        if (updatePublishedAt) {
          storyUpdate.publishedAt = selectedPublishedAt
            ? new Date(selectedPublishedAt).toISOString()
            : null;
        } else if (updatePhase && selectedPhase === 'READY_FOR_PUBLISH') {
          // Clear publishedAt when shifting to READY_FOR_PUBLISH
          storyUpdate.publishedAt = null;
        }

        const { error: storyError } = await supabase
          .from('stories')
          .update(withUpdateTimestamp(toDbKeys(storyUpdate)))
          .in('id', selectedIds);

        if (storyError) {
          throw new Error(`Failed to update stories: ${storyError.message}`);
        }

        logger.info('Bulk updated story publishedAt', {
          count: selectedIds.length,
          publishedAt: storyUpdate.publishedAt,
        });
      }

      // Success - reset and close
      handleClose();
      onComplete();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      logger.error('Bulk edit failed', { error: message });
    } finally {
      setIsProcessing(false);
    }
  };

  const allPhases = Object.keys(STORY_TRANS_PHASE_CONFIG) as StoryTransPhase[];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Stories</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Update {selectedIds.length} stor{selectedIds.length > 1 ? 'ies' : 'y'}:
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Phase Selection */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Checkbox
              checked={updatePhase}
              onChange={e => setUpdatePhase(e.target.checked)}
              disabled={isProcessing}
            />
            <FormControl
              disabled={!updatePhase || isProcessing}
              sx={{ minWidth: 200, flex: 1 }}
            >
              <InputLabel>Translation Phase</InputLabel>
              <Select
                value={selectedPhase}
                label="Translation Phase"
                onChange={e => setSelectedPhase(e.target.value as StoryTransPhase)}
              >
                {allPhases.map(phase => {
                  const config = getStoryTransPhaseConfig(phase);
                  return (
                    <MenuItem key={phase} value={phase}>
                      {`${config.emoji} ${config.label}`}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          {/* Published Date Selection */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Checkbox
              checked={updatePublishedAt}
              onChange={e => setUpdatePublishedAt(e.target.checked)}
              disabled={isProcessing}
            />
            <TextField
              label="Published Date"
              type="datetime-local"
              value={selectedPublishedAt}
              onChange={handleDateChange}
              disabled={!updatePublishedAt || isProcessing}
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </Box>

        {updatePhase && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Phase will be updated for all translations of the selected stories.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={isProcessing || (!updatePhase && !updatePublishedAt)}
          startIcon={isProcessing ? <CircularProgress size={16} /> : null}
        >
          {isProcessing ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
